'use client'

import { Group, Stack } from '@mantine/core'
import { Carousel } from '@mantine/carousel'
import type { Sku, SkuPriceOptionsResponse } from '@/types/interfaces/sku'
import {
    VisitOptionByType,
    type VisitAvailabilityData,
} from '@/components/VisitOptions/VisitOptionByType'
import type { VisitType } from '@/components/VisitOptions/visitOptionsHelpers'

interface DefaultVisitOptionsLayoutProps {
    types: VisitType[]
    sku: Sku
    prices: SkuPriceOptionsResponse | null
    userHomeTimezone: string | null
    availabilityData: VisitAvailabilityData
    availabilityLoading: boolean
    theme: 'default' | 'tight'
    isMobile: boolean
    onClick: () => void
    onOfficeVisitClick: (e: React.MouseEvent) => void
}

/**
 * The non-in-person layout. Picks one of three presentations based on
 * viewport + theme:
 *   - desktop:                 horizontal `Group`
 *   - mobile + `tight` theme:  vertical `Stack`
 *   - mobile + default theme:  drag-free `Carousel`
 */
export function DefaultVisitOptionsLayout({
    types,
    sku,
    prices,
    userHomeTimezone,
    availabilityData,
    availabilityLoading,
    theme,
    isMobile,
    onClick,
    onOfficeVisitClick,
}: DefaultVisitOptionsLayoutProps) {
    const renderOption = (type: VisitType) => (
        <VisitOptionByType
            key={type}
            type={type}
            sku={sku}
            prices={prices}
            userHomeTimezone={userHomeTimezone}
            availabilityData={availabilityData}
            availabilityLoading={availabilityLoading}
            theme={theme}
            onClick={onClick}
            onOfficeVisitClick={onOfficeVisitClick}
        />
    )

    if (!isMobile) {
        return (
            <Group gap="md" align="stretch">
                {types.map(renderOption)}
            </Group>
        )
    }

    if (theme === 'tight') {
        return <Stack gap="md">{types.map(renderOption)}</Stack>
    }

    return (
        <Carousel
            slideSize="0%"
            align="start"
            slideGap={16}
            withControls={false}
            dragFree={true}
            styles={{
                viewport: { overflow: 'visible' },
            }}
        >
            {types.map((type) => (
                <Carousel.Slide key={type}>{renderOption(type)}</Carousel.Slide>
            ))}
        </Carousel>
    )
}
